import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope, faArrowLeft, faSpinner, faCheckCircle,
  faTimesCircle, faForward, faClock, faSync,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../context/AuthContext';
import MainLayout from '../Layout/MainLayout';

const STATUS_STYLE = {
  completed: { bg: '#e8f5ee', fg: '#1f8a3a', icon: faCheckCircle, label: 'Completed' },
  running:   { bg: '#e6f0ff', fg: '#2a5cad', icon: faSync,        label: 'Running' },
  queued:    { bg: '#fff8e1', fg: '#a87c00', icon: faClock,       label: 'Queued' },
  skipped:   { bg: '#eef0f4', fg: '#666',    icon: faForward,     label: 'Skipped' },
  failed:    { bg: '#fdecec', fg: '#c0392b', icon: faTimesCircle, label: 'Failed' },
};

const EmailRouterHistory = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { load(); }, []);  // eslint-disable-line

  const load = async () => {
    try {
      setLoading(true);
      const resp = await fetch('/api/email-router/history?limit=200', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Failed to load history');
      const data = await resp.json();
      setItems(data.items || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout>
      <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
        <button onClick={() => navigate('/email-router')} style={backBtn}>
          <FontAwesomeIcon icon={faArrowLeft} /> Back
        </button>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
          <h1 style={{ margin: 0 }}>
            <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: 12, color: '#5b8def' }} />
            Email Router — History
          </h1>
          <button onClick={load} style={btnSecondary}><FontAwesomeIcon icon={faSync} /> Refresh</button>
        </div>

        {loading && (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <FontAwesomeIcon icon={faSpinner} spin /> Loading…
          </div>
        )}
        {error && <div style={errorBox}><FontAwesomeIcon icon={faTimesCircle} /> {error}</div>}

        {!loading && !error && items.length === 0 && (
          <div style={emptyState}>
            <h3>No emails processed yet</h3>
            <p style={{ color: '#666' }}>Once the router starts polling, processed emails will appear here.</p>
          </div>
        )}

        {!loading && !error && items.length > 0 && (
          <div style={tableWrap}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f7f9fc', textAlign: 'left' }}>
                  <th style={th}>When</th>
                  <th style={th}>Subject</th>
                  <th style={th}>From</th>
                  <th style={th}>Matched</th>
                  <th style={th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => {
                  const s = STATUS_STYLE[r.status] || STATUS_STYLE.queued;
                  return (
                    <tr key={r.id} style={{ borderTop: '1px solid #eef0f4' }}>
                      <td style={td}>{r.created_at ? new Date(r.created_at).toLocaleString() : '—'}</td>
                      <td style={td} title={r.subject}>{(r.subject || '').slice(0, 60)}</td>
                      <td style={td}>{r.sender}</td>
                      <td style={td}>{r.matched_ai_label || <span style={{ color: '#bbb' }}>—</span>}</td>
                      <td style={td}>
                        <span style={pill(s.bg, s.fg)}>
                          <FontAwesomeIcon icon={s.icon} /> {s.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

const btnSecondary = {
  background: 'white', color: '#5b8def', border: '1px solid #5b8def',
  padding: '8px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600,
};
const backBtn = {
  background: 'transparent', border: 'none', color: '#5b8def',
  cursor: 'pointer', fontSize: 14, padding: 0,
};
const tableWrap = {
  background: 'white', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden', marginTop: 16,
};
const th = { padding: '12px 16px', fontSize: 13, color: '#555', fontWeight: 600 };
const td = { padding: '14px 16px', fontSize: 14, color: '#222' };
const errorBox = {
  background: '#fdecec', color: '#c0392b', padding: 12, borderRadius: 6, marginTop: 16,
};
const emptyState = {
  textAlign: 'center', padding: 48, background: 'white', borderRadius: 8, marginTop: 16,
};
const pill = (bg, fg) => ({
  background: bg, color: fg, padding: '4px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
});

export default EmailRouterHistory;
